import React from 'react';
import type { FC } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { isLoggedIn } from '@helpCenter/state/selectors/user';
import FallbackSuggestedRequestForms from 'assets/images/fallback-suggested-request-forms.svg';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { AutoScaledImage } from 'view/common/auto-scaled-image';
import { ElementRoot, ElementWidth } from 'view/common/element-root';
import recentFormsMessages from 'view/help-center/recent-forms/messages';
import { IconCardLayout } from 'view/layout/icon-card-layout';
import { useIsPageInEditMode, useIsPageInPreviewMode } from 'view/layout-next/controllers';
import * as fonts from 'view/styles/fonts';
import EmptyState from '@atlaskit/empty-state';
import { Stack, Box, xcss } from '@atlaskit/primitives';
import type { suggestedRequestForms_HelpLayoutListOfHelpCenterSuggestedRequestFormsAtomicElement$key as SuggestedRequestedForms } from './__generated__/suggestedRequestForms_HelpLayoutListOfHelpCenterSuggestedRequestFormsAtomicElement.graphql';
import messages from './messages';
import { RecentFormsListItem } from './recent-forms-list-item/recent-forms-list-item';

const selector = createSelector(isLoggedIn, (isUserLoggedIn) => ({ isUserLoggedIn }));

const Component: FC<{ forms: SuggestedRequestedForms; isUserLoggedIn: boolean }> = ({ forms, isUserLoggedIn }) => {
    const { suggestedRequestTypes } = useFragment<SuggestedRequestedForms>(
        graphql`
            fragment suggestedRequestForms_HelpLayoutListOfHelpCenterSuggestedRequestFormsAtomicElement on HelpLayoutSuggestedRequestFormsListElement {
                suggestedRequestTypes {
                    ...recentFormsListItem_on_RequestForm
                    id
                    iconUrl
                }
            }
        `,
        forms
    );
    const { formatMessage } = useIntl();
    di(useIsPageInEditMode);
    di(useIsPageInPreviewMode);

    const [isPageInEditMode] = useIsPageInEditMode();
    const [isPageInPreviewMode] = useIsPageInPreviewMode();

    if (!suggestedRequestTypes?.length && isPageInEditMode && !isPageInPreviewMode) {
        return (
            <Box xcss={nonDraggableStyle}>
                <EmptyState
                    header=""
                    maxImageWidth={200}
                    description={formatMessage(messages.emptyStateDescription)}
                    imageUrl={FallbackSuggestedRequestForms}
                    width="wide"
                />
            </Box>
        );
    }

    if (suggestedRequestTypes?.length) {
        return (
            <ElementRoot elementWidth={ElementWidth.FULL_WIDTH}>
                <Stack xcss={containerStyles}>
                    {/* TODO: remove connection to the user selector once we have the label field on the Element. */}
                    <Header>
                        {isUserLoggedIn
                            ? formatMessage(recentFormsMessages.headerTitle)
                            : formatMessage(recentFormsMessages.headerTitleAnon)}
                    </Header>
                    <Stack as="ul" xcss={listStyles}>
                        {suggestedRequestTypes.map((requestForm) => {
                            const Icon =
                                requestForm.iconUrl == null ? null : (
                                    <AutoScaledImage src={requestForm.iconUrl} alt="" />
                                );
                            const LinkAndDescription = <RecentFormsListItem requestForm={requestForm} />;
                            return (
                                <Box as="li" xcss={itemStyles} key={`recentRequestSuggestion_${requestForm.id}`}>
                                    <IconCardLayout padding={0} leftIcon={Icon} content={LinkAndDescription} />
                                </Box>
                            );
                        })}
                    </Stack>
                </Stack>
            </ElementRoot>
        );
    }
    return null;
};

const listStyles = xcss({
    marginTop: 'space.0',
    paddingLeft: 'space.0',
    rowGap: 'space.150',
    listStyleType: 'none',
});

const containerStyles = xcss({
    rowGap: 'space.200',
    width: '100%',
    maxWidth: '840px',
    marginInline: 'auto',
    boxSizing: 'border-box',
    paddingInline: 'space.600',
});

const itemStyles = xcss({
    wordWrap: 'break-word',
    marginTop: 'space.0',
});

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Header = styled.h2`
    ${fonts.h500};
`;

const nonDraggableStyle = xcss({
    pointerEvents: 'none',
});

export const SuggestedRequestForms = connect(selector)(Component);
